import React from 'react';
import Route from '../Route';

import BotsContainer from './BotsContainer';
import BotSettings from './BotSettings';
import Train from './Train';
import Answers from './Answers';
import BotAnswersImportReview from './Answers/Imports/BotAnswersImportReview';
import Answer from './Answer';
import Skills from './Skills';
import NewSkill from './NewSkill';
import SkillPage from './SkillPage';
import BotsRoute from './BotsRoute';
import GeckoFeatures from 'services/gecko-features';
import NotFound from 'routes/404/NotFound';

const sanitizeId = (id) => {
  return id === 'new' ? 'new' : Number(id);
};
const extractBotId = (params) => sanitizeId(params.botId);
const extractSkillId = (params) => sanitizeId(params.skillId);

const botCheckedAction = (action) => (...args) => {
  const botEnabled = GeckoFeatures.can('chat-bot');
  if (!botEnabled) {
    return <NotFound />;
  }
  return action(...args);
};

const routes = new Route({
  path: '/bots',
  children: [
    {
      name: 'bots',
      path: '/',
      action: botCheckedAction(() => (
        <BotsContainer>
          <BotsRoute />
        </BotsContainer>
      )),
    },
    {
      name: 'train',
      path: '/:botId/train',
      action: botCheckedAction(({ params }) => (
        <BotsContainer
          className="BotsContainer-Table"
          botId={extractBotId(params)}
        >
          <Train botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'answers',
      path: '/:botId/answers',
      action: botCheckedAction(({ params }) => (
        <BotsContainer
          subsection="answers"
          className="BotsContainer-Table"
          botId={extractBotId(params)}
        >
          <Answers botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'answers-import-review',
      path: '/:botId/answers-import-review/:id',
      action: botCheckedAction(({ params }) => (
        <BotsContainer
          className="BotsContainer-Table"
          botId={extractBotId(params)}
        >
          <BotAnswersImportReview botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'answer',
      path: '/:botId/answers/:id',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <Answer botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'clone answer',
      path: '/:botId/answers/clone/:id',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <Answer botId={extractBotId(params)} cloning={true} />
        </BotsContainer>
      )),
    },
    {
      name: 'skills',
      path: '/:botId/skills',
      action: botCheckedAction(({ params }) => (
        <BotsContainer
          subsection="skills"
          className="BotsContainer-Table"
          botId={extractBotId(params)}
        >
          <Skills botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'new skill',
      path: '/:botId/skills/new',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <NewSkill botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
    {
      name: 'skill',
      path: '/:botId/skills/data_capture/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="data_capture"
          />
        </BotsContainer>
      )),
    },
    {
      name: 'skill',
      path: '/:botId/skills/workflow_action/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="workflow_action"
          />
        </BotsContainer>
      )),
    },
    {
      name: 'skill',
      path: '/:botId/skills/suggest_answer/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="suggest_answer"
          />
        </BotsContainer>
      )),
    },
    {
      name: 'skill',
      path: '/:botId/skills/display_list/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="display_list"
          />
        </BotsContainer>
      )),
    },
    {
      name: 'skill',
      path: '/:botId/skills/gecko_form/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="gecko_form"
          />
        </BotsContainer>
      )),
    },
    {
      name: 'clone skill',
      path: '/:botId/skills/data_capture/clone/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="data_capture"
            cloning={true}
          />
        </BotsContainer>
      )),
    },
    {
      name: 'clone skill',
      path: '/:botId/skills/workflow_action/clone/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="workflow_action"
            cloning={true}
          />
        </BotsContainer>
      )),
    },
    {
      name: 'clone skill',
      path: '/:botId/skills/suggest_answer/clone/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="suggest_answer"
            cloning={true}
          />
        </BotsContainer>
      )),
    },
    {
      name: 'clone skill',
      path: '/:botId/skills/display_list/clone/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="display_list"
            cloning={true}
          />
        </BotsContainer>
      )),
    },
    {
      name: 'clone skill',
      path: '/:botId/skills/gecko_form/clone/:skillId',
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <SkillPage
            id={extractSkillId(params)}
            botId={extractBotId(params)}
            type="gecko_form"
            cloning={true}
          />
        </BotsContainer>
      )),
    },
    {
      name: 'bot settings',
      path: '/:botId/settings',
      requires: ['admin', 'supervisor'],
      action: botCheckedAction(({ params }) => (
        <BotsContainer botId={extractBotId(params)}>
          <BotSettings botId={extractBotId(params)} />
        </BotsContainer>
      )),
    },
  ],
});

export default routes;
